import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Play, RefreshCw, AlertTriangle, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const POLL_INTERVAL_MS = 2000;

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

function fmtPct(flagged, total) {
  if (!total) return '-';
  return `${((flagged / total) * 100).toFixed(1)}%`;
}

// Read-only diagnostic (CLAUDE.md Prompt 5) -- no Apply/mutating action
// anywhere on this panel. Counterparty/amount/ref population counts
// always cover the full population in one pass; direction integrity (live
// Drive reads) is budget-boxed and resumable, mirroring
// DrivePathAuditPanel's Preview -- a truncated run just needs another
// click of Start to continue from its cursor.
export function InvoiceMetadataCensusPanel() {
  const [dispatching, setDispatching] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null);
  const [failed, setFailed] = useState(null);
  const [polling, setPolling] = useState(false);
  const pollRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setPolling(false);
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const pollJob = useCallback((jobId) => {
    stopPolling();
    setPolling(true);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await diagnosticsAPI.invoiceMetadataCensusJobStatus(jobId);
        if (data.status === 'completed') {
          stopPolling();
          setProgress(null);
          setFailed(null);
          setResult(data.result);
          toast.success('Invoice metadata census complete');
        } else if (data.status === 'failed') {
          stopPolling();
          setProgress((prev) => data.progress || prev);
          setFailed(data.error);
          toast.error(data.error ?? 'census failed');
        } else if (data.status === 'running') {
          setProgress(data.progress || null);
        }
      } catch (err) {
        stopPolling();
        toast.error(getErrorMessage(err));
      }
    }, POLL_INTERVAL_MS);
  }, [stopPolling]);

  const start = async () => {
    setDispatching(true);
    setFailed(null);
    try {
      const { data } = await diagnosticsAPI.invoiceMetadataCensusStart();
      if (data.status === 'already_running') {
        toast.info('A census run is already in progress');
      }
      pollJob(data.job_id);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDispatching(false);
    }
  };

  // On mount, show the latest completed/failed run without forcing a new
  // scan -- an operator revisiting the page shouldn't have to re-trigger
  // a multi-minute live-Drive pass just to see the last result.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.invoiceMetadataCensusJobLatest();
        const job = data?.job;
        if (!job) return;
        if (job.status === 'completed') setResult(job.result);
        else if (job.status === 'running') pollJob(job._id);
        else if (job.status === 'failed') {
          setFailed(job.error);
          setProgress(job.progress || null);
        }
      } catch {
        // Non-fatal: panel just starts empty.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderBucket = (label, bucket) => {
    if (!bucket) return null;
    const rows = Object.entries(bucket.by_source || {}).sort((a, b) => b[1].flagged - a[1].flagged);
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold">{label}</h4>
          <Badge variant={bucket.flagged > 0 ? 'destructive' : 'secondary'}>
            {fmtInt(bucket.flagged)} / {fmtInt(bucket.total)} ({fmtPct(bucket.flagged, bucket.total)})
          </Badge>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Source</TableHead>
              <TableHead className="text-right">Flagged</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Rate</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(([source, b]) => (
              <TableRow key={source}>
                <TableCell>{source}</TableCell>
                <TableCell className="text-right">{fmtInt(b.flagged)}</TableCell>
                <TableCell className="text-right">{fmtInt(b.total)}</TableCell>
                <TableCell className="text-right">{fmtPct(b.flagged, b.total)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Search className="h-4 w-4" />
          Invoice Metadata Extraction Quality Census
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-sm text-muted-foreground">
          Read-only. Counts Unknown counterparty, missing/zero amount, and missing ref across the
          full invoice/receipt population, and cross-checks the stored direction field against the
          actual Drive filename token and actual Drive folder segment. Writes nothing to Drive, the
          database, or any doc/filename/folder.
        </p>

        <div className="flex items-center gap-3">
          <Button onClick={start} disabled={dispatching || polling}>
            {dispatching || polling ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            {polling ? 'Running...' : 'Start Census'}
          </Button>
          {result?.direction_integrity?.truncated && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <RefreshCw className="h-3 w-3" />
              Direction-integrity pass was budget-truncated -- click Start again to continue.
            </span>
          )}
        </div>

        {progress && (
          <div className="text-xs text-muted-foreground">
            {progress.stage === 'direction_integrity'
              ? `Direction integrity: ${fmtInt(progress.scanned)} / ${fmtInt(progress.total)} scanned`
              : `Scanned ${fmtInt(progress.scanned)}`}
          </div>
        )}

        {failed && (
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4" />
            {failed}
          </div>
        )}

        {result && (
          <div className="space-y-6">
            <div className="text-xs text-muted-foreground">
              Population: {fmtInt(result.total_population)} documents
              {result.metadata_scan_truncated && ' (metadata scan truncated by hard cap)'}
              &nbsp;&middot;&nbsp;generated {result.generated_at}
            </div>

            {renderBucket('Counterparty Unknown', result.counterparty)}
            {renderBucket('Amount 0 / Missing', result.amount)}
            {renderBucket('Ref NoRef / Missing', result.ref)}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold">Direction Integrity</h4>
                <Badge variant="secondary">
                  {fmtInt(result.direction_integrity?.candidates_with_file_url)} candidates with file_url,{' '}
                  {fmtInt(result.direction_integrity?.scanned)} scanned
                  {result.direction_integrity?.truncated ? ' (truncated)' : ''}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                Each row: how many documents had this exact combination of stored `type` bucket,
                actual filename Client/Supplier token, actual Drive folder Inbound/Outbound segment,
                the canonical direction services/drive_path_audit.py:218 actually computes (naive),
                and what it would compute with the receivable/payable translation applied (corrected).
              </p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Combination</TableHead>
                    <TableHead className="text-right">Count</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(result.direction_integrity?.combo_counts || {})
                    .sort((a, b) => b[1] - a[1])
                    .map(([combo, count]) => (
                      <TableRow key={combo}>
                        <TableCell className="font-mono text-xs">{combo}</TableCell>
                        <TableCell className="text-right">{fmtInt(count)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              <div className="text-xs text-muted-foreground">
                no_file_url: {fmtInt(result.direction_integrity?.no_file_url_count)} &middot; outside_tree:{' '}
                {fmtInt(result.direction_integrity?.outside_tree_count)} &middot; errors:{' '}
                {fmtInt(result.direction_integrity?.error_count)}
              </div>
            </div>

            {result.direction_integrity?.mismatch_samples?.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold">Direction Mismatch Samples</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Ref</TableHead>
                      <TableHead>Stored</TableHead>
                      <TableHead>Filename</TableHead>
                      <TableHead>Folder</TableHead>
                      <TableHead>Naive canon.</TableHead>
                      <TableHead>Corrected canon.</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.direction_integrity.mismatch_samples.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell className="font-mono text-xs">{s.id}</TableCell>
                        <TableCell>{s.source}</TableCell>
                        <TableCell>{s.invoice_number}</TableCell>
                        <TableCell>{s.stored_direction}</TableCell>
                        <TableCell>{s.filename_direction}</TableCell>
                        <TableCell>{s.folder_direction}</TableCell>
                        <TableCell>{s.naive_canonical_direction}</TableCell>
                        <TableCell>{s.corrected_canonical_direction}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {result.tracer_bullets?.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold">Tracer Bullets (named samples)</h4>
                {result.tracer_bullets.map((tb) => (
                  <Card key={tb.label} className="bg-muted/30">
                    <CardContent className="pt-4 space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{tb.label}</span>
                        <Badge variant={tb.found ? 'secondary' : 'destructive'}>
                          {tb.found ? 'found' : 'not found in sandbox'}
                        </Badge>
                      </div>
                      {tb.found && (
                        <pre className="text-xs whitespace-pre-wrap break-all bg-background rounded p-2 overflow-x-auto">
                          {JSON.stringify(
                            {
                              stored_fields: tb.stored_fields,
                              stored_direction_bucket: tb.stored_direction_bucket,
                              naive_canonical_direction: tb.naive_canonical_direction,
                              corrected_canonical_direction: tb.corrected_canonical_direction,
                              live_drive_check: tb.live_drive_check,
                            },
                            null,
                            2,
                          )}
                        </pre>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {result.unknown_counterparty_samples_by_source &&
              Object.keys(result.unknown_counterparty_samples_by_source).length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold">Unknown-Counterparty Samples (up to 20/source)</h4>
                  {Object.entries(result.unknown_counterparty_samples_by_source).map(([source, samples]) => (
                    <div key={source} className="space-y-1">
                      <div className="text-xs font-medium text-muted-foreground">{source}</div>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>ID</TableHead>
                            <TableHead>Entity</TableHead>
                            <TableHead>Ref</TableHead>
                            <TableHead>Issue date</TableHead>
                            <TableHead>Has file_url</TableHead>
                            <TableHead>synced_credential_id</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {samples.map((s) => (
                            <TableRow key={s.id}>
                              <TableCell className="font-mono text-xs">{s.id}</TableCell>
                              <TableCell>{s.entity}</TableCell>
                              <TableCell>{s.invoice_number || '-'}</TableCell>
                              <TableCell>{s.issue_date || '-'}</TableCell>
                              <TableCell>{s.has_file_url ? 'yes' : 'no'}</TableCell>
                              <TableCell className="font-mono text-xs">{s.synced_credential_id || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ))}
                </div>
              )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
